import { useNavigate } from "react-router-dom";
import { LogOut, Settings, ShieldCheck, Building2, User } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";

/**
 * Profile/account menu — current account basics + logout only.
 * Settings routes to a future-phase placeholder.
 */
export const ProfileMenu = () => {
  const { user, workspace, workspaceRole, platformRole, logout } = useAuth();
  const navigate = useNavigate();
  const initials = `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase() || "A";
  const isStaff = Boolean(platformRole);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={AUTH.userMenu}
          className="grid h-9 w-9 place-items-center rounded-full bg-brand-navy text-sm font-semibold text-white transition-transform hover:scale-105"
          aria-label="Account menu"
        >
          {initials}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="font-semibold text-foreground">{user?.first_name} {user?.last_name}</span>
          <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 text-xs text-muted-foreground">
          {isStaff ? (
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-accent" />
              Platform staff · {platformRole === "super_admin" ? "Super Admin" : "Support Admin"}
            </span>
          ) : (
            <>
              <span className="flex items-center gap-2">
                <User className="h-3.5 w-3.5 text-accent" /> Role · <span className="capitalize">{workspaceRole || "agent"}</span>
              </span>
              {workspace && (
                <span className="mt-1 flex items-center gap-2">
                  <Building2 className="h-3.5 w-3.5 text-accent" /> {workspace.name}
                </span>
              )}
            </>
          )}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="profile-settings" className="gap-2.5">
          <Settings className="h-4 w-4" /> Settings
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleLogout} data-testid={AUTH.logout} className="gap-2.5 text-destructive focus:text-destructive">
          <LogOut className="h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
